/**
 * The KnowIt adapter for W5: projects, Members/Groups writes, and workspace governance
 * (Docit.Auth administration, web-source policy, webhooks).
 *
 * Three route families, three envelopes (§B.1 of the wave spec):
 *  - `projects.*` and `people.*` (members/groups writes) are Outline-lineage dot-RPC —
 *    `{ data[, policies] }` — and ride the `rpc()` lane (`send()` on the parent).
 *  - `governance.sourcePolicy.*` / `governance.webhooks.*` are REST verbs on owner-built
 *    controllers, answering BARE bodies with a 204 on delete — the `rest()` lane.
 *  - `governance.auth.*` / `governance.organizations.*` proxy straight through to KnowIt.Auth
 *    and come back as Auth's own body, byte-for-byte — also the `rest()` lane, because it is
 *    already 204-tolerant and never assumes an envelope.
 *
 * Kept as its own module, handed a transport, for the same reason `knowitAutomation.ts` and
 * `knowitOps.ts` are: the parent class is already 1000+ lines, and every wire quirk here is
 * this wave's alone.
 */
import {
  type AuthIdentity,
  type AuthPolicy,
  type AuthRole,
  type AuthTeam,
  type AuthTeamMember,
  type AuthTeamMembership,
  type EntityAbilities,
  type GrantLevel,
  type GroupMemberRole,
  type GroupMembership,
  type InviteResult,
  type MemberInvite,
  type MemberRole,
  type Organization,
  PROJECT_STATUS_WIRE,
  type Project,
  type ProjectDraft,
  type ProjectGroupMembership,
  type ProjectMembership,
  type ProjectPatch,
  type ProjectStatus,
  projectStatusFromWire,
  type SourceApproval,
  type SourceApprovalStatus,
  type SourcePolicy,
  type Webhook,
  type WebhookDelivery,
  type WebhookDraft,
} from "~/api/governanceTypes";
import type { Collection, Group, Member } from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" ? (value as Json) : {};
}
function arr(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}
function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function strArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}
function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function bool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}
function emojiOrNull(icon: unknown): string | null {
  return typeof icon === "string" && icon.length <= 4 && !/^[a-z-]+$/i.test(icon) ? icon : null;
}
function grantLevel(value: unknown): GrantLevel {
  return value === "read_write" || value === "admin" ? value : "read";
}

/** A `{ id, abilities }` policy row, resolved against the response's `policies` array. */
function abilitiesFor(policies: unknown, id: string): Record<string, boolean> {
  const row = arr(policies).find((entry) => entry.id === id);
  return row ? (obj(row.abilities) as Record<string, boolean>) : {};
}
function translate(
  source: Record<string, boolean>,
  map: Record<string, string>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [from, to] of Object.entries(map)) {
    if (source[from] === true) {
      out[to] = true;
    }
  }
  return out;
}

export interface GovernanceTransport {
  /** The `send()` lane: dot-RPC, `{ data[, policies] }`. */
  rpc<T = unknown>(
    path: string,
    body?: Record<string, unknown>,
  ): Promise<{ data: T; policies?: unknown }>;
  /** The `rest()` lane: REST verbs and Auth passthroughs, 204-tolerant, never enveloped. */
  rest(path: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
  /** `governance.auth.sync()` supplies the identity provider fields itself (§A.1.1 A2). */
  viewer(): Promise<{ email: string; name: string; avatarUrl: string | null }>;
}

function toProject(raw: Json): Project {
  return {
    id: str(raw.id),
    teamId: str(raw.teamId),
    groupId: strOrNull(raw.groupId),
    name: str(raw.name, "Untitled"),
    description: strOrNull(raw.description),
    status: projectStatusFromWire(str(raw.status, "Active")),
    icon: strOrNull(raw.icon),
    color: strOrNull(raw.color),
    startDate: strOrNull(raw.startDate),
    endDate: strOrNull(raw.endDate),
    ownerId: strOrNull(raw.ownerId),
    createdById: str(raw.createdById),
    archivedAt: strOrNull(raw.archivedAt),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, str(raw.createdAt, new Date(0).toISOString())),
    etag: str(raw.etag),
  };
}

function toMemberBasic(raw: Json): Member {
  return {
    id: str(raw.id),
    name: str(raw.name),
    email: str(raw.email),
    avatarUrl: strOrNull(raw.avatarUrl),
    role: str(raw.role, "member"),
    suspended: bool(raw.isSuspended),
    lastActiveAt: strOrNull(raw.lastActiveAt),
    abilities: {},
  };
}

function toCollectionBasic(raw: Json, policies: unknown): Collection {
  return {
    id: str(raw.id),
    name: str(raw.name, "Untitled"),
    emoji: emojiOrNull(raw.icon),
    description: str(raw.description),
    index: num(raw.index),
    projectId: strOrNull(raw.projectId),
    archivedAt: strOrNull(raw.archivedAt),
    abilities: translate(abilitiesFor(policies, str(raw.id)), {
      update: "update",
      delete: "delete",
      createDocument: "createDocument",
    }),
  };
}

function toGroupBasic(raw: Json, policies: unknown): Group {
  return {
    id: str(raw.id),
    name: str(raw.name),
    memberCount: num(raw.memberCount),
    abilities: translate(abilitiesFor(policies, str(raw.id)), {
      update: "update",
      delete: "delete",
    }),
  };
}

function toGroupMembership(raw: Json): GroupMembership {
  const userId = str(raw.userId);
  const groupId = str(raw.groupId);
  return {
    id: str(raw.id, `${userId}-${groupId}`),
    userId,
    groupId,
    role: raw.role === "lead" ? "lead" : "member",
    user: null,
  };
}

function toAuthIdentity(raw: unknown): AuthIdentity {
  const r = obj(raw);
  return {
    userId: str(r.userId),
    email: str(r.email),
    name: str(r.name),
    avatarUrl: strOrNull(r.avatarUrl),
    teams: arr(r.teams).map((t) => ({
      teamId: str(t.teamId),
      role: str(t.role),
      status: str(t.status),
    })),
    attributes: obj(r.attributes),
  };
}

/** `syncUser().teams[]` carries `teamId`; `getUserTeams()`/`listTeams()` carry `id` (§E-7). */
function toAuthTeamMembership(raw: Json): AuthTeamMembership {
  return {
    teamId: str(raw.id ?? raw.teamId),
    ...(typeof raw.name === "string" ? { name: raw.name } : {}),
    ...(typeof raw.slug === "string" ? { slug: raw.slug } : {}),
    ...(typeof raw.organizationId === "string" ? { organizationId: raw.organizationId } : {}),
    role: str(raw.role),
    status: str(raw.status),
  };
}

function toAuthTeam(raw: Json): AuthTeam {
  return {
    id: str(raw.id),
    name: str(raw.name),
    slug: str(raw.slug),
    organizationId: str(raw.organizationId),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
  };
}

function toAuthTeamMember(raw: Json): AuthTeamMember {
  return {
    userId: str(raw.userId),
    teamId: str(raw.teamId),
    status: str(raw.status),
    role: str(raw.role),
    ...(typeof raw.name === "string" ? { name: raw.name } : {}),
    ...(typeof raw.email === "string" ? { email: raw.email } : {}),
    ...(typeof raw.teamName === "string" ? { teamName: raw.teamName } : {}),
  };
}

function toAuthRole(raw: Json): AuthRole {
  return {
    id: str(raw.id),
    name: str(raw.name),
    slug: str(raw.slug),
    description: strOrNull(raw.description),
    isSystem: bool(raw.isSystem),
    permissions: strArray(raw.permissions),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
  };
}

function toAuthPolicy(raw: Json): AuthPolicy {
  return {
    id: str(raw.id),
    name: str(raw.name),
    description: strOrNull(raw.description),
    resourceType: str(raw.resourceType, "*"),
    effect: raw.effect === "deny" ? "deny" : "permit",
    priority: num(raw.priority, 100),
    actions: strArray(raw.actions),
    conditions: obj(raw.conditions),
    isActive: bool(raw.isActive, true),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
  };
}

function toOrganization(raw: Json): Organization {
  // kms's `normalizeOrg` reads both `id`/`Id` defensively; Auth's controllers emit camelCase
  // (no naming policy override), but the shape is another service's — keep the tolerance.
  return {
    id: str(raw.id ?? raw.Id),
    name: str(raw.name ?? raw.Name),
    domain: strOrNull(raw.domain ?? raw.Domain),
    settingsJson: raw.settingsJson ? obj(raw.settingsJson) : null,
    createdAt: str(raw.createdAt ?? raw.CreatedAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt ?? raw.UpdatedAt, new Date(0).toISOString()),
    planTier: str(raw.planTier ?? raw.PlanTier, "free"),
    planClaimVersion: num(raw.planClaimVersion ?? raw.PlanClaimVersion),
  };
}

function toSourcePolicy(raw: Json): SourcePolicy {
  const engine = raw.engineDefault;
  return {
    engineDefault: engine === "google" || engine === "bing" ? engine : "duckduckgo",
    engineLocked: bool(raw.engineLocked),
    domainMode: raw.domainMode === "allow" || raw.domainMode === "deny" ? raw.domainMode : "off",
    domains: strArray(raw.domains),
    requireSourceApproval: bool(raw.requireSourceApproval),
    maxSourcesPerRun: num(raw.maxSourcesPerRun),
    maxCrawlDepth: num(raw.maxCrawlDepth),
  };
}

function toSourceApproval(raw: Json): SourceApproval {
  return {
    id: str(raw.id),
    domain: str(raw.domain),
    sampleUrl: strOrNull(raw.sampleUrl),
    thinkerId: strOrNull(raw.thinkerId),
    documentId: strOrNull(raw.documentId),
    status: raw.status === "approved" || raw.status === "rejected" ? raw.status : "pending",
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
  };
}

function toWebhook(raw: Json): Webhook {
  return {
    id: str(raw.id),
    teamId: str(raw.teamId),
    createdById: str(raw.createdById),
    url: str(raw.url),
    secret: str(raw.secret),
    events: strArray(raw.events),
    enabled: bool(raw.enabled, true),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
    lastDeliveryAt: strOrNull(raw.lastDeliveryAt),
  };
}

function toWebhookDelivery(raw: Json): WebhookDelivery {
  return {
    id: str(raw.id),
    subscriptionId: str(raw.subscriptionId),
    eventType: str(raw.eventType),
    status: str(raw.status),
    attempt: num(raw.attempt),
    statusCode: typeof raw.statusCode === "number" ? raw.statusCode : null,
    error: strOrNull(raw.error),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
    nextAttemptAt: strOrNull(raw.nextAttemptAt),
    completedAt: strOrNull(raw.completedAt),
  };
}

export function createKnowItGovernance(transport: GovernanceTransport) {
  const projectBody = (
    input: ProjectDraft | (ProjectPatch & { id?: string }),
  ): Record<string, unknown> => {
    const body: Record<string, unknown> = {};
    if ("name" in input && input.name !== undefined) body.name = input.name;
    if (input.description !== undefined) body.description = input.description;
    if (input.groupId !== undefined) body.groupId = input.groupId;
    if (input.icon !== undefined) body.icon = input.icon;
    if (input.color !== undefined) body.color = input.color;
    if (input.startDate !== undefined) body.startDate = input.startDate;
    if (input.endDate !== undefined) body.endDate = input.endDate;
    if ("status" in input && input.status !== undefined) {
      body.status = PROJECT_STATUS_WIRE[input.status as ProjectStatus];
    }
    return body;
  };

  const projects = {
    list: async (query?: {
      status?: ProjectStatus;
      groupId?: string;
      limit?: number;
    }): Promise<Project[]> => {
      const envelope = await transport.rpc<Json[]>("projects.list", {
        limit: query?.limit ?? 100,
        ...(query?.status ? { status: PROJECT_STATUS_WIRE[query.status] } : {}),
        ...(query?.groupId ? { groupId: query.groupId } : {}),
      });
      return arr(envelope.data).map(toProject);
    },

    mine: async (): Promise<Project[]> => {
      const envelope = await transport.rpc<Json[]>("projects.mine");
      return arr(envelope.data).map(toProject);
    },

    /** Batch-resolve the real per-user ability map (§E-8) — `projects.list` carries none. */
    abilities: async (ids: string[]): Promise<EntityAbilities[]> => {
      if (ids.length === 0) {
        return [];
      }
      const envelope = await transport.rpc<Json[]>("policies.list", {
        entities: ids.map((id) => ({ type: "project", id })),
      });
      return arr(envelope.data).map((row) => ({
        id: str(row.id),
        abilities: obj(row.abilities) as Record<string, boolean>,
      }));
    },

    create: async (input: ProjectDraft): Promise<Project> => {
      const envelope = await transport.rpc<Json>("projects.create", {
        name: input.name,
        ...projectBody(input),
      });
      return toProject(envelope.data);
    },

    /**
     * `etag` is accepted for a future `If-Match` header (the response carries the *next* one
     * in the body's own `etag` field either way); wiring the header is deliberately deferred
     * — the transport's `rest()`/`request()` do not expose response headers today, and adding
     * that plumbing for one call was out of scope for this wave (see the wave record).
     */
    update: async (input: ProjectPatch & { id: string; etag?: string }): Promise<Project> => {
      const { id } = input;
      const envelope = await transport.rpc<Json>("projects.update", { id, ...projectBody(input) });
      return toProject(envelope.data);
    },

    archive: async (id: string): Promise<void> => {
      await transport.rpc("projects.archive", { id });
    },
    restore: async (id: string): Promise<void> => {
      await transport.rpc("projects.restore", { id });
    },
    delete: async (id: string): Promise<void> => {
      await transport.rpc("projects.delete", { id });
    },

    people: {
      list: async (id: string): Promise<{ memberships: ProjectMembership[]; users: Member[] }> => {
        const envelope = await transport.rpc<Json>("projects.memberships", {
          id,
          offset: 0,
          limit: 200,
        });
        const data = obj(envelope.data);
        return {
          memberships: arr(data.memberships).map((m) => ({
            userId: str(m.userId),
            permission: grantLevel(m.permission),
          })),
          users: arr(data.users).map(toMemberBasic),
        };
      },
      add: async (input: { id: string; userId: string; permission: GrantLevel }): Promise<void> => {
        await transport.rpc("projects.add_user", input);
      },
      remove: async (input: { id: string; userId: string }): Promise<void> => {
        await transport.rpc("projects.remove_user", input);
      },
      groups: async (id: string): Promise<ProjectGroupMembership[]> => {
        const envelope = await transport.rpc<Json>("projects.group_memberships", {
          id,
          offset: 0,
          limit: 200,
        });
        const data = obj(envelope.data);
        return arr(data.groupMemberships).map((row) => ({
          id: str(row.id),
          groupId: str(row.groupId),
          permission: grantLevel(row.permission),
          group: row.group ? { id: str(obj(row.group).id), name: str(obj(row.group).name) } : null,
        }));
      },
      addGroup: async (input: {
        id: string;
        groupId: string;
        permission: GrantLevel;
      }): Promise<void> => {
        await transport.rpc("projects.add_group", input);
      },
      removeGroup: async (input: { id: string; groupId: string }): Promise<void> => {
        await transport.rpc("projects.remove_group", input);
      },
    },

    linkCollection: async (input: {
      collectionId: string;
      projectId: string | null;
    }): Promise<Collection> => {
      const envelope = await transport.rpc<Json>("collections.linkProject", {
        id: input.collectionId,
        projectId: input.projectId,
      });
      return toCollectionBasic(envelope.data, envelope.policies);
    },

    anchorGroup: async (input: { groupId: string; projectId: string | null }): Promise<Group> => {
      const envelope = await transport.rpc<Json>("groups.anchorProject", {
        id: input.groupId,
        projectId: input.projectId,
      });
      return toGroupBasic(envelope.data, envelope.policies);
    },
  };

  const people = {
    invite: async (invites: MemberInvite[]): Promise<InviteResult> => {
      const envelope = await transport.rpc<Json>("users.invite", { invites });
      const data = obj(envelope.data);
      return {
        sent: arr(data.sent).map((row) => ({ email: str(row.email) })),
        users: arr(data.users).map((row) => ({
          id: str(row.id),
          email: str(row.email),
          name: str(row.name),
        })),
      };
    },
    resendInvite: async (id: string): Promise<void> => {
      await transport.rpc("users.resendInvite", { id });
    },
    setRole: async (input: { id: string; role: MemberRole }): Promise<Member> => {
      const envelope = await transport.rpc<Json>("users.update_role", input);
      return toMemberBasic(envelope.data);
    },
    suspend: async (id: string): Promise<Member> => {
      const envelope = await transport.rpc<Json>("users.suspend", { id });
      return toMemberBasic(envelope.data);
    },
    activate: async (id: string): Promise<Member> => {
      const envelope = await transport.rpc<Json>("users.activate", { id });
      return toMemberBasic(envelope.data);
    },
    remove: async (id: string): Promise<void> => {
      await transport.rpc("users.delete", { id });
    },
    groups: {
      create: async (input: { name: string; externalId?: string }): Promise<Group> => {
        const envelope = await transport.rpc<Json>("groups.create", input);
        return toGroupBasic(envelope.data, envelope.policies);
      },
      update: async (input: { id: string; name?: string; externalId?: string }): Promise<Group> => {
        const envelope = await transport.rpc<Json>("groups.update", input);
        return toGroupBasic(envelope.data, envelope.policies);
      },
      delete: async (id: string): Promise<void> => {
        await transport.rpc("groups.delete", { id });
      },
      members: async (id: string): Promise<{ memberships: GroupMembership[]; users: Member[] }> => {
        const envelope = await transport.rpc<Json>("groups.memberships", {
          id,
          offset: 0,
          limit: 200,
        });
        const data = obj(envelope.data);
        return {
          memberships: arr(data.groupMemberships).map(toGroupMembership),
          users: arr(data.users).map(toMemberBasic),
        };
      },
      addMember: async (input: { id: string; userId: string }): Promise<void> => {
        await transport.rpc("groups.add_user", input);
      },
      removeMember: async (input: { id: string; userId: string }): Promise<void> => {
        await transport.rpc("groups.remove_user", input);
      },
      setMemberRole: async (input: {
        id: string;
        userId: string;
        role: GroupMemberRole;
      }): Promise<void> => {
        await transport.rpc("groups.setMemberRole", input);
      },
    },
  };

  const governance = {
    auth: {
      sync: async (): Promise<AuthIdentity> => {
        const viewer = await transport.viewer();
        const body = await transport.rest("knowitauth.syncUser", "POST", {
          email: viewer.email,
          name: viewer.name,
          provider: "google",
          ...(viewer.avatarUrl ? { avatarUrl: viewer.avatarUrl } : {}),
        });
        return toAuthIdentity(body);
      },
      myTeams: async (): Promise<AuthTeamMembership[]> => {
        const body = await transport.rest("knowitauth.getUserTeams", "POST", {});
        return arr(body).map(toAuthTeamMembership);
      },
      teams: {
        list: async (): Promise<AuthTeam[]> => {
          const body = await transport.rest("knowitauth.listTeams", "POST", {});
          return arr(body).map(toAuthTeam);
        },
        members: async (teamId: string): Promise<AuthTeamMember[]> => {
          const body = await transport.rest("knowitauth.getTeamMembers", "POST", { teamId });
          return arr(body).map(toAuthTeamMember);
        },
        create: async (input: {
          name: string;
          slug: string;
          organizationId: string;
        }): Promise<AuthTeam> => {
          const body = await transport.rest("knowitauth.createTeam", "POST", input);
          return toAuthTeam(obj(body));
        },
        update: async (input: {
          teamId: string;
          name: string;
          slug: string;
        }): Promise<AuthTeam> => {
          const body = await transport.rest("knowitauth.updateTeam", "POST", input);
          return toAuthTeam(obj(body));
        },
        remove: async (teamId: string): Promise<void> => {
          await transport.rest("knowitauth.deleteTeam", "POST", { teamId });
        },
      },
      roles: {
        list: async (): Promise<AuthRole[]> => {
          const body = await transport.rest("knowitauth.listRoles", "POST", {});
          return arr(body).map(toAuthRole);
        },
        create: async (input: {
          name: string;
          slug: string;
          description?: string;
          permissions: string[];
        }): Promise<AuthRole> => {
          const body = await transport.rest("knowitauth.createRole", "POST", input);
          return toAuthRole(obj(body));
        },
        update: async (input: {
          roleId: string;
          name: string;
          slug: string;
          description?: string;
          permissions: string[];
        }): Promise<AuthRole> => {
          const body = await transport.rest("knowitauth.updateRole", "POST", input);
          return toAuthRole(obj(body));
        },
        remove: async (roleId: string): Promise<void> => {
          await transport.rest("knowitauth.deleteRole", "POST", { roleId });
        },
      },
      policies: {
        list: async (): Promise<AuthPolicy[]> => {
          const body = await transport.rest("knowitauth.listPolicies", "POST", {});
          return arr(body)
            .map(toAuthPolicy)
            .sort((a, b) => b.priority - a.priority);
        },
        create: async (input: {
          name: string;
          description?: string;
          resourceType: string;
          effect: "permit" | "deny";
          priority: number;
          actions: string[];
          conditions: Record<string, unknown>;
          isActive: boolean;
        }): Promise<AuthPolicy> => {
          const body = await transport.rest("knowitauth.createPolicy", "POST", input);
          return toAuthPolicy(obj(body));
        },
        update: async (input: {
          policyId: string;
          name: string;
          description?: string;
          resourceType: string;
          effect: "permit" | "deny";
          priority: number;
          actions: string[];
          conditions: Record<string, unknown>;
          isActive: boolean;
        }): Promise<AuthPolicy> => {
          const body = await transport.rest("knowitauth.updatePolicy", "POST", input);
          return toAuthPolicy(obj(body));
        },
        remove: async (policyId: string): Promise<void> => {
          await transport.rest("knowitauth.deletePolicy", "POST", { policyId });
        },
      },
    },

    organizations: {
      list: async (): Promise<Organization[]> => {
        const body = await transport.rest("analytics/organizations", "GET");
        return arr(body).map(toOrganization);
      },
      create: async (input: {
        name: string;
        domain?: string;
        settingsJson?: Record<string, unknown>;
      }): Promise<Organization> => {
        const body = await transport.rest("analytics/organizations", "POST", input);
        return toOrganization(obj(body));
      },
      update: async (input: {
        id: string;
        name: string;
        domain?: string;
        settingsJson?: Record<string, unknown>;
      }): Promise<Organization> => {
        const { id, ...rest } = input;
        const body = await transport.rest(
          `analytics/organizations/${encodeURIComponent(id)}`,
          "PUT",
          rest,
        );
        return toOrganization(obj(body));
      },
      remove: async (id: string): Promise<void> => {
        await transport.rest(`analytics/organizations/${encodeURIComponent(id)}`, "DELETE");
      },
    },

    sourcePolicy: {
      get: async (): Promise<SourcePolicy> =>
        toSourcePolicy(obj(await transport.rest("source-policy", "GET"))),
      save: async (policy: SourcePolicy): Promise<SourcePolicy> => {
        const body = await transport.rest(
          "source-policy",
          "PUT",
          policy as unknown as Record<string, unknown>,
        );
        return toSourcePolicy(obj(body));
      },
      approvals: async (status?: SourceApprovalStatus): Promise<SourceApproval[]> => {
        const qs = status ? `?status=${encodeURIComponent(status)}` : "?status=pending";
        const body = await transport.rest(`source-policy/approvals${qs}`, "GET");
        return arr(body).map(toSourceApproval);
      },
      decide: async (input: {
        id: string;
        status: "approved" | "rejected";
      }): Promise<SourceApproval> => {
        const body = await transport.rest(
          `source-policy/approvals/${encodeURIComponent(input.id)}/decide`,
          "POST",
          { status: input.status },
        );
        return toSourceApproval(obj(body));
      },
    },

    webhooks: {
      list: async (): Promise<Webhook[]> => {
        const body = await transport.rest("webhooks", "GET");
        return arr(body).map(toWebhook);
      },
      create: async (input: WebhookDraft): Promise<Webhook> => {
        const body = await transport.rest("webhooks", "POST", {
          url: input.url,
          events: input.events,
          ...(input.enabled === undefined ? {} : { enabled: input.enabled }),
        });
        return toWebhook(obj(body));
      },
      /** A whole-document PUT — the caller must echo back `url`/`events` even to flip `enabled`. */
      update: async (input: WebhookDraft & { id: string }): Promise<Webhook> => {
        const body = await transport.rest(`webhooks/${encodeURIComponent(input.id)}`, "PUT", {
          url: input.url,
          events: input.events,
          enabled: input.enabled ?? true,
        });
        return toWebhook(obj(body));
      },
      remove: async (id: string): Promise<void> => {
        await transport.rest(`webhooks/${encodeURIComponent(id)}`, "DELETE");
      },
      rotateSecret: async (id: string): Promise<Webhook> => {
        const body = await transport.rest(
          `webhooks/${encodeURIComponent(id)}/rotate-secret`,
          "POST",
        );
        return toWebhook(obj(body));
      },
      deliveries: async (id: string, limit = 50): Promise<WebhookDelivery[]> => {
        const body = await transport.rest(
          `webhooks/${encodeURIComponent(id)}/deliveries?limit=${limit}`,
          "GET",
        );
        return arr(body).map(toWebhookDelivery);
      },
    },
  };

  return { projects, people, governance };
}
